/*
 * Движения шарика переключателя, ползунка и плашки сегментов — перенос
 * `control(T)` из `liquid-ui.js` макета v6 без стекла движка (вариант «В»
 * из проб без стекла, 3 ноября). В покое шарик — сплошная капсула. Нажали —
 * он сам пружиной растёт до размера «в руке», едет за пальцем, тянется вдоль
 * движения на скорости и, отпущенный, сжимается обратно. Пока шарик в руке,
 * на органе стоит `data-held` (тень глубже — в `ui.css`).
 *
 * Числа из макета без правок: пружина положения 520/44 с шагом 1/240,
 * растяжение до 0,28 ширины «в руке»; размер ведёт своя пружина 600/46 — та,
 * что была у линзы движка.
 */

export type ThumbKind = "toggle" | "slider" | "seg";

export interface ThumbSpec {
  kind: ThumbKind;
  /** Сам орган: на нём слушаются нажатия и клавиши, по его ширине идёт путь. */
  root: HTMLElement;
  /** Шарик (плашка у сегментов): ему пишутся ширина и высота. */
  knob: HTMLElement;
  /** Что едет за положением: им пишется `left`. */
  wraps: HTMLElement[];
  /** Ширина и высота шарика в покое. */
  rest: [number, number];
  /** Ширина и высота шарика в руке. */
  hold: [number, number];
  /** Значение: доля 0..1 у ползунка, 0/1 у переключателя, номер доли у сегментов. */
  val: number;
  /** Долей у сегментов. */
  n?: number;
  /** Поле дорожки у сегментов. */
  pad?: number;
  /** Куда прилипает доля ползунка. По умолчанию — никуда. */
  snap?: (f: number) => number;
  /** Шаг стрелки у ползунка, долей. */
  keyStep?: number;
  /** Положение `fx` и цель `ft`, обе долями пути, — на каждом кадре. */
  onPaint?: (fx: number, ft: number) => void;
  /** Новое значение, выбранное человеком. */
  onChange?: (val: number) => void;
  /** Подписи сегментов: какая доля сейчас под плашкой. */
  label?: (i: number) => void;
}

const reduced = () =>
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

export class Thumb {
  private readonly T: ThumbSpec;
  private x = 0;
  private v = 0;
  private tgt = 0;
  private val: number;
  private active = false;
  private raf = 0;
  private last = 0;
  private moved = false;
  private st = 0;
  private scale = 1;
  private sx = 0;
  private x0 = 0;
  private down = false;
  private tapped = false;
  private disabled = false;
  private timers: number[] = [];
  private off = new AbortController();
  private ro: ResizeObserver;
  /** Размер шарика и его скорость. */
  private sw: number;
  private sh: number;
  private vw = 0;
  private vh = 0;

  constructor(T: ThumbSpec) {
    this.T = T;
    this.val = T.val;
    [this.sw, this.sh] = T.rest;
    this.x = this.tgt = this.toX(this.val);
    this.bind();
    this.draw();
    this.paint();

    let width = T.root.clientWidth;
    this.ro = new ResizeObserver(() => {
      if (T.root.clientWidth === width) return;
      width = T.root.clientWidth;
      this.x = this.tgt = this.toX(this.val);
      this.paint();
    });
    this.ro.observe(T.root);
  }

  set(val: number) {
    if (val === this.val) return;
    this.val = val;
    this.tgt = this.toX(val);
    this.T.label?.(val);
    this.kick();
  }

  setDisabled(on: boolean) {
    this.disabled = on;
    if (on && this.active) this.up();
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    this.timers.forEach((t) => clearTimeout(t));
    this.off.abort();
    this.ro.disconnect();
  }

  private W() {
    return this.T.root.clientWidth;
  }

  private range(): [number, number] {
    const { kind, rest, pad = 0, n = 1 } = this.T;
    const W = this.W();
    if (kind === "slider") return [0, W];
    if (kind === "toggle") return [rest[0] / 2 + 2, W - rest[0] / 2 - 2];
    const cell = (W - 2 * pad) / n / 2;
    return [pad + cell, W - pad - cell];
  }

  private toX(val: number) {
    const [a, b] = this.range();
    const { kind, n = 1 } = this.T;
    return kind === "seg" ? a + (b - a) * (n > 1 ? val / (n - 1) : 0) : a + (b - a) * val;
  }

  private frac(x: number) {
    const [a, b] = this.range();
    return Math.max(0, Math.min(1, (x - a) / (b - a || 1)));
  }

  private nearest(x: number) {
    return Math.round(this.frac(x) * ((this.T.n ?? 1) - 1));
  }

  private snapped(f: number) {
    return this.T.snap ? this.T.snap(f) : f;
  }

  private draw() {
    const el = this.T.knob;
    el.style.width = this.sw.toFixed(2) + "px";
    el.style.height = this.sh.toFixed(2) + "px";
  }

  private paint() {
    this.T.wraps.forEach((w) => {
      w.style.left = this.x.toFixed(2) + "px";
    });
    this.T.onPaint?.(this.frac(this.x), this.frac(this.tgt));
    if (this.T.kind === "seg" && this.active) this.T.label?.(this.nearest(this.x));
  }

  private step = (now: number) => {
    const T = this.T;
    const rm = reduced();
    const dt = Math.min(1 / 30, this.last ? (now - this.last) / 1000 : 1 / 60);
    this.last = now;
    if (rm) {
      this.x = this.tgt;
      this.v = 0;
    } else {
      const ns = Math.max(1, Math.ceil(dt * 240));
      const h = dt / ns;
      for (let s = 0; s < ns; s++) {
        this.v += ((this.tgt - this.x) * 520 - this.v * 44) * h;
        this.x += this.v * h;
      }
    }
    const want = this.active && !rm ? Math.min(T.hold[0] * 0.28, Math.abs(this.v) * 0.025) : 0;
    this.st += (want - this.st) * Math.min(1, dt * 8);
    if (this.st < 0.05) this.st = 0;

    // Размер — своей пружиной, как у капли движка.
    const tw = this.active ? T.hold[0] + this.st : T.rest[0];
    const th = this.active ? T.hold[1] - this.st * 0.2 : T.rest[1];
    if (rm) {
      this.sw = tw;
      this.sh = th;
      this.vw = this.vh = 0;
    } else {
      const ns = Math.max(1, Math.ceil(dt * 240));
      const h = dt / ns;
      for (let s = 0; s < ns; s++) {
        this.vw += ((tw - this.sw) * 600 - this.vw * 46) * h;
        this.sw += this.vw * h;
        this.vh += ((th - this.sh) * 600 - this.vh * 46) * h;
        this.sh += this.vh * h;
      }
    }
    this.draw();
    this.paint();

    const gap = Math.abs(this.sh - T.rest[1]) + Math.abs(this.sw - T.rest[0]);
    if (this.active || gap > 0.5 || Math.abs(this.vw) > 1 || Math.abs(this.tgt - this.x) > 0.2 || Math.abs(this.v) > 1) {
      this.raf = requestAnimationFrame(this.step);
    } else {
      this.raf = 0;
      this.last = 0;
      if (T.kind === "seg") this.tgt = this.toX(this.val);
      this.x = this.tgt;
      [this.sw, this.sh] = T.rest;
      this.vw = this.vh = 0;
      this.draw();
      this.paint();
    }
  };

  private kick() {
    if (!this.raf) this.raf = requestAnimationFrame(this.step);
  }

  private setActive(on: boolean) {
    this.active = on;
    this.T.root.toggleAttribute("data-held", on);
    if (!on && this.T.kind === "seg") this.T.label?.(this.val);
    this.kick();
  }

  private commit(val: number) {
    const changed = val !== this.val;
    this.val = val;
    this.tgt = this.toX(val);
    this.T.label?.(val);
    if (changed) this.T.onChange?.(val);
    this.kick();
  }

  private later(fn: () => void, ms: number) {
    this.timers.push(window.setTimeout(fn, ms));
  }

  private localX(e: PointerEvent) {
    const r = this.T.root.getBoundingClientRect();
    this.scale = r.width / this.W() || 1;
    return (e.clientX - r.left) / this.scale;
  }

  private up = () => {
    if (!this.active || !this.down) return;
    this.down = false;
    const { kind } = this.T;
    if (kind === "toggle") this.commit(this.moved ? (this.frac(this.tgt) > 0.5 ? 1 : 0) : this.val ? 0 : 1);
    if (kind === "seg") this.commit(this.nearest(this.tgt));
    this.later(() => this.setActive(false), kind === "slider" ? 0 : 140);
    this.later(() => {
      this.tapped = false;
    }, 0);
  };

  private flash(val: number) {
    this.setActive(true);
    this.commit(val);
    this.later(() => this.setActive(false), 160);
  }

  private bind() {
    const { root, kind } = this.T;
    const opt = { signal: this.off.signal };

    root.addEventListener(
      "pointerdown",
      (e) => {
        if (e.button > 0 || this.disabled) return;
        e.preventDefault();
        try {
          root.setPointerCapture(e.pointerId);
        } catch {
          // Захват не обязателен: отпускание слушается и на окне.
        }
        root.focus({ preventScroll: true });
        this.down = true;
        this.tapped = true;
        this.sx = e.clientX;
        this.x0 = this.x;
        this.moved = false;
        const lx = this.localX(e);
        const [a, b] = this.range();
        if (kind === "slider") {
          this.val = this.snapped(this.frac(lx));
          this.tgt = this.toX(this.val);
        }
        if (kind === "seg") this.tgt = Math.max(a, Math.min(b, lx));
        this.setActive(true);
      },
      opt,
    );

    root.addEventListener(
      "pointermove",
      (e) => {
        if (!this.active || !this.down) return;
        const dx = (e.clientX - this.sx) / (this.scale || 1);
        if (Math.abs(dx) > 3) this.moved = true;
        const lx = this.localX(e);
        const [a, b] = this.range();
        if (kind === "slider") {
          this.val = this.snapped(this.frac(lx));
          this.tgt = this.toX(this.val);
        } else if (kind === "toggle") this.tgt = Math.max(a, Math.min(b, this.x0 + dx));
        else this.tgt = Math.max(a, Math.min(b, lx));
        this.kick();
      },
      opt,
    );

    root.addEventListener("pointerup", this.up, opt);
    root.addEventListener("pointercancel", this.up, opt);
    root.addEventListener("lostpointercapture", this.up, opt);
    window.addEventListener("pointerup", this.up, { ...opt, capture: true });
    window.addEventListener("blur", this.up, opt);
    document.documentElement.addEventListener("mouseleave", this.up, opt);

    root.addEventListener(
      "click",
      (e) => {
        if (this.tapped) {
          this.tapped = false;
          return;
        }
        if (this.disabled || kind !== "toggle") return;
        e.preventDefault();
        this.flash(this.val ? 0 : 1);
      },
      opt,
    );

    root.addEventListener(
      "keydown",
      (e) => {
        if (this.disabled) return;
        const k = e.key;
        const dir = k === "ArrowRight" || k === "ArrowUp" ? 1 : k === "ArrowLeft" || k === "ArrowDown" ? -1 : 0;
        if (kind === "slider") {
          const st = this.T.keyStep ?? 0.05;
          let next: number | null = null;
          if (dir) next = this.val + dir * st;
          else if (k === "PageUp" || k === "PageDown") next = this.val + (k === "PageUp" ? 10 : -10) * st;
          else if (k === "Home") next = 0;
          else if (k === "End") next = 1;
          if (next === null) return;
          e.preventDefault();
          this.val = this.snapped(Math.max(0, Math.min(1, next)));
          this.tgt = this.toX(this.val);
          this.kick();
        }
        if (kind === "toggle" && (k === " " || k === "Enter")) {
          e.preventDefault();
          this.flash(this.val ? 0 : 1);
        }
        if (kind === "seg" && dir) {
          e.preventDefault();
          this.flash(Math.max(0, Math.min((this.T.n ?? 1) - 1, this.val + dir)));
        }
      },
      opt,
    );
    root.addEventListener(
      "keyup",
      (e) => {
        if (e.key === " ") e.preventDefault();
      },
      opt,
    );
  }
}
